import {
  IonButton,
  IonButtons,
  IonContent,
  IonFab,
  IonHeader,
  IonIcon,
  IonLabel,
  IonTitle,
  IonToolbar,
  useIonActionSheet
} from "@ionic/react";
import {closeOutline, ellipsisVerticalCircleOutline} from "ionicons/icons";
import {useTranslation} from "react-i18next";
import type {
  PurchaseBuyerFragment,
  PurchaseDocumentFragment,
  PurchaseProductFragment,
  PurchaseSupplierFragment
} from "app/gql/graphqlSchema";
import {isNil} from "app/utils/stdlib";
import type {MaybeNil} from "app/utils/types";
import {toBigNumber} from "app/utils/validator";
import {DateDisplay} from "app/employee/DateDisplay";
import {luxonToShortDate, parseAnyDate} from "app/utils/dates";
import {Fragment, useCallback} from "react";
import {styled} from "styled-components";
import {useModalContext} from "app/employee/incdoc/FstIonModal";
import {
  PrintH3,
  TransposedHeader,
  TransposedTable,
  TransposedTableDivider,
  TransposedTableLabel,
  TransposedTableValue
} from "app/components/print/view/printStyles";
import {ListPadding} from "app/employee/incdoc/ListPadding";
import {HomeBackButton} from "app/employee/menu/HomeBackButton";
import {excelIcon} from "app/utils/icons/ionicFixedIcons";

const Section = styled.div`
  margin-bottom: 1rem;
`;

const TotalsRow = styled.div<{$bold?: boolean}>`
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  padding: 0.25rem 0;
  font-weight: ${({$bold}) => ($bold ? "700" : "400")};
`;

const formatAmount = (value: MaybeNil<string>, currency: MaybeNil<string>) => {
  if (isNil(value)) return "—";
  const curr = currency ?? "";
  return `${toBigNumber(value).toFixed(2)} ${curr}`.trim();
};

const PartyInfo = ({
  party,
  label
}: {
  party: MaybeNil<PurchaseSupplierFragment | PurchaseBuyerFragment>;
  label: string;
}) => {
  const {t} = useTranslation();
  if (!party) return null;
  const fields = [
    {label: t("purchase-doc.field.name", {defaultValue: "Наименование"}), value: party.name},
    {label: t("purchase-doc.field.eik", {defaultValue: "ЕИК"}), value: party.eik},
    {label: t("purchase-doc.field.address", {defaultValue: "Адрес"}), value: party.address},
    {label: t("purchase-doc.field.mol", {defaultValue: "МОЛ"}), value: party.mol}
  ].filter((f) => f.value);

  if (fields.length === 0) return null;

  return (
    <Section>
      <TransposedTable>
        <TransposedHeader>{label}</TransposedHeader>
        {fields.map((f) => (
          <Fragment key={f.label}>
            <TransposedTableLabel>{f.label}</TransposedTableLabel>
            <TransposedTableValue>{f.value}</TransposedTableValue>
            <TransposedTableDivider />
          </Fragment>
        ))}
      </TransposedTable>
    </Section>
  );
};

const ProductItem = ({product, index}: {product: PurchaseProductFragment; index: number}) => {
  const {t} = useTranslation();
  const fields = [
    {label: t("purchase-doc.field.item-number", {defaultValue: "Код"}), value: product.itemNumber},
    {label: t("purchase-doc.field.lot-number", {defaultValue: "Партида"}), value: product.lotNumber},
    {
      label: t("purchase-doc.field.expiration-date", {defaultValue: "Срок"}),
      value: product.expirationDate,
      isDate: true
    },
    {
      label: t("purchase-doc.field.quantity", {defaultValue: "Количество"}),
      value: isNil(product.quantity) ? null : toBigNumber(product.quantity).toString()
    },
    {label: t("purchase-doc.field.unit", {defaultValue: "Единица"}), value: product.unit},
    {
      label: t("purchase-doc.field.unit-price", {defaultValue: "Единична цена"}),
      value: isNil(product.unitPrice) ? null : toBigNumber(product.unitPrice).toFixed(2)
    },
    {
      label: t("purchase-doc.field.total-price", {defaultValue: "Сума"}),
      value: isNil(product.totalPrice) ? null : toBigNumber(product.totalPrice).toFixed(2)
    }
  ].filter((f) => f.value);

  return (
    <>
      <TransposedHeader>
        {index + 1}. {product.name ?? "—"}
      </TransposedHeader>
      {fields.map((f) => (
        <Fragment key={f.label}>
          <TransposedTableLabel>{f.label}</TransposedTableLabel>
          <TransposedTableValue>{f.isDate ? <DateDisplay dt={f.value} /> : f.value}</TransposedTableValue>
          <TransposedTableDivider />
        </Fragment>
      ))}
    </>
  );
};

interface Props {
  purchaseDocument: PurchaseDocumentFragment;
}

export const PurchaseDocumentPreview = ({purchaseDocument}: Props) => {
  const {t} = useTranslation();
  const modalContext = useModalContext();

  const [presentActionSheet] = useIonActionSheet();

  const {invoiceNumber, date, supplier, buyer, products, currency, amount, vat, total, excelUrl} = purchaseDocument;

  const showActions = useCallback(async () => {
    await presentActionSheet({
      header: t("incdoc.purchase-doc.actions-title", {defaultValue: "Действия"}),
      buttons: [
        {
          text: t("incdoc.purchase-doc.export-excel", {defaultValue: "Експорт като Excel"}),
          icon: excelIcon,
          handler: () => {
            window.open(excelUrl, "_blank", "noopener");
          }
        },
        {
          text: t("g.close"),
          icon: closeOutline,
          role: "cancel" as const
        }
      ]
    });
  }, [excelUrl, presentActionSheet, t]);

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <HomeBackButton />
          <IonTitle>{t("incdoc.purchase-doc.preview-title", {defaultValue: "Преглед на разпознаването"})}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => modalContext?.dismiss()}>
              <IonIcon icon={closeOutline} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {(invoiceNumber ?? date) && (
          <Section>
            <PrintH3>
              {[
                invoiceNumber &&
                  t("incdoc.purchase-doc.invoice-number", {defaultValue: "Документ № {{num}}", num: invoiceNumber}),
                date && luxonToShortDate(parseAnyDate(date))
              ]
                .filter(Boolean)
                .join(" / ")}
            </PrintH3>
          </Section>
        )}

        <PartyInfo label={t("purchase-doc.party.supplier", {defaultValue: "Продавач"})} party={supplier} />
        <PartyInfo label={t("purchase-doc.party.buyer", {defaultValue: "Купувач"})} party={buyer} />

        {products && products.length > 0 && (
          <>
            <Section>
              <strong>{t("purchase-doc.products", {defaultValue: "Продукти"})}</strong>
            </Section>
            <Section>
              <TransposedTable>
                {products.map((product, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <ProductItem index={i} key={i} product={product} />
                ))}
              </TransposedTable>
            </Section>
          </>
        )}

        {(!isNil(amount) || !isNil(vat) || !isNil(total)) && (
          <Section>
            {!isNil(amount) && (
              <TotalsRow>
                <span>{t("purchase-doc.totals.base", {defaultValue: "Основа"})}:</span>
                <span>{formatAmount(amount, currency)}</span>
              </TotalsRow>
            )}
            {!isNil(vat) && (
              <TotalsRow>
                <span>{t("purchase-doc.totals.vat", {defaultValue: "ДДС"})}:</span>
                <span>{formatAmount(vat, currency)}</span>
              </TotalsRow>
            )}
            {!isNil(total) && (
              <TotalsRow $bold>
                <span>{t("purchase-doc.totals.total", {defaultValue: "Общо"})}:</span>
                <span>{formatAmount(total, currency)}</span>
              </TotalsRow>
            )}
          </Section>
        )}
        <ListPadding />
        {excelUrl && (
          <IonFab horizontal="end" slot="fixed" vertical="bottom">
            <IonButton onClick={showActions} shape="round">
              <IonIcon icon={ellipsisVerticalCircleOutline} slot="start" />
              <IonLabel>{t("g.action", {defaultValue: "Действия"})}</IonLabel>
            </IonButton>
          </IonFab>
        )}
      </IonContent>
    </>
  );
};
